// Sorties du patrimoine (second temps, D-144) : demandes à valider, pièce justificative jointe par le
// service gestionnaire, décision du maire ou président (validation avec pièce, ou refus motivé) ; demandes
// tranchées consultables.
import { CODES_ETATS_SORTIE, ETATS_SORTIE, type SortieBien } from '@cc/contrats';
import { Bandeau, Bouton, Champ, DialogueConfirmation, EtatVide, FichierChamp, Filtres, Lien, LienBouton, Panneau, PuceEtat, useFiltresPersistes } from '@cc/ui';
import { ArrowLeft, LogOut } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { deciderSortie, deposerPieceSortie, INVALIDER_PATRIMOINE, useSorties } from './api';
import { aujourdhuiIso, TON_SORTIE } from './logique';
import commun from '../../commun/commun.module.css';
import styles from '../proprete/proprete.module.css';

function Demande({ s }: { s: SortieBien }) {
  const { contexte } = useSession();
  const [fichier, setFichier] = useState<File | null>(null);
  const [decision, setDecision] = useState<'valider' | 'refuser' | null>(null);
  const [motif, setMotif] = useState('');
  const [dateSortie, setDateSortie] = useState(s.date_sortie ?? aujourdhuiIso());
  const piece = useAction({ executer: () => deposerPieceSortie(s.id, fichier as File), succes: 'Pièce justificative jointe à la demande.', invalider: INVALIDER_PATRIMOINE, apres: () => setFichier(null) });
  const decider = useAction({
    executer: () => deciderSortie(s.id, decision === 'valider' ? { decision: 'valider', date_sortie: dateSortie, motif: motif || undefined } : { decision: 'refuser', motif }),
    succes: decision === 'valider' ? `Sortie validée : le bien ${s.bien.reference} est sorti du patrimoine.` : 'Demande de sortie refusée.',
    invalider: INVALIDER_PATRIMOINE,
    apres: () => setDecision(null),
  });
  const ouverte = s.etat === 'demandee';
  return (
    <Panneau titre={`${s.bien.reference} · ${s.bien.designation}`} niveau={2}>
      <dl className={commun.definitions}>
        <dt>Mode</dt><dd>{s.mode_libelle}</dd>
        <dt>Motif</dt><dd>{s.motif}</dd>
        <dt>Demandée</dt><dd>le {dateHeure(s.demandee_le)}{s.demandee_par ? ` par ${s.demandee_par}` : ''}</dd>
        <dt>Pièce</dt><dd>{s.piece_document_id ? 'jointe' : <span className={styles.motif}>manquante</span>}</dd>
        <dt>État</dt><dd><PuceEtat ton={TON_SORTIE[s.etat]} libelle={s.etat_libelle} />{s.decision_le ? ` le ${dateHeure(s.decision_le)}${s.decision_par ? ` par ${s.decision_par}` : ''}` : ''}</dd>
        {s.date_sortie && s.etat === 'validee' ? <><dt>Sortie le</dt><dd>{date(s.date_sortie)}</dd></> : null}
        {s.decision_motif ? <><dt>Décision</dt><dd>{s.decision_motif}</dd></> : null}
      </dl>
      <p><Lien href={`/patrimoine/biens/${s.bien.id}`}>Fiche du bien</Lien></p>
      {ouverte && peut(contexte, 'patrimoine', 'transmettre') && !s.piece_document_id ? (
        <div className={styles.grilleFormulaire}>
          <FichierChamp libelle="Pièce justificative (procès-verbal, acte)" multiple={false} accept=".pdf,.jpg,.jpeg,.png" tailleMax={20 * 1024 * 1024} fichiers={fichier ? [fichier] : []} onFichiers={(f) => setFichier(f[0] ?? null)} />
          <Bouton variante="secondaire" chargement={piece.enCours} disabled={!fichier} onClick={() => piece.lancer(undefined)}>Joindre la pièce</Bouton>
          {piece.messageErreur ? <Bandeau type="erreur">{piece.messageErreur}</Bandeau> : null}
        </div>
      ) : null}
      {ouverte && peut(contexte, 'patrimoine', 'signer') ? (
        <span className={commun.ligne}>
          <Bouton variante="principal" disabled={!s.piece_document_id} onClick={() => setDecision('valider')}>Valider la sortie</Bouton>
          <Bouton variante="secondaire" onClick={() => setDecision('refuser')}>Refuser</Bouton>
        </span>
      ) : null}
      <DialogueConfirmation ouvert={decision !== null} titre={decision === 'valider' ? 'Valider la sortie du patrimoine' : 'Refuser la sortie'}
        message={decision === 'valider' ? 'Le bien passe à l\'état « sorti » à la date indiquée ; il reste consultable et n\'est plus réservable.' : 'Le bien retrouve son état d\'avant la demande.'}
        libelleConfirmer={decision === 'valider' ? 'Valider' : 'Refuser'} variante={decision === 'valider' ? 'principal' : 'danger'} enCours={decider.enCours}
        onConfirmer={() => decider.lancer(undefined)} onAnnuler={() => setDecision(null)} confirmationDesactivee={decision === 'refuser' && motif.trim().length < 3}>
        {decision === 'valider' ? <Champ libelle="Date de sortie" type="date" value={dateSortie} onChange={(e) => setDateSortie(e.target.value)} required /> : null}
        <Champ libelle={decision === 'valider' ? 'Observation' : 'Motif du refus'} value={motif} onChange={(e) => setMotif(e.target.value)} required={decision === 'refuser'} facultatif={decision === 'valider'} />
        {decider.messageErreur ? <Bandeau type="erreur">{decider.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </Panneau>
  );
}

export function PageSorties() {
  const [filtres, setFiltres] = useFiltresPersistes('patrimoine-sorties', { etat: 'demandee' });
  const sorties = useSorties(filtres.etat || undefined);
  return (
    <>
      <EnTetePage etiquette="Patrimoine" titre="Sorties du patrimoine" description="Sortie en deux temps : la demande motivée du service gestionnaire, puis la décision du maire ou président avec la pièce justificative."
        actions={<LienBouton href="/patrimoine" icone={ArrowLeft} variante="secondaire">Inventaire du patrimoine</LienBouton>} />
      <Filtres groupes={[{ id: 'etat', libelle: 'État', options: [{ valeur: '', libelle: 'Toutes' }, ...CODES_ETATS_SORTIE.map((e) => ({ valeur: e, libelle: ETATS_SORTIE[e] }))] }]} valeurs={filtres} onChanger={setFiltres} />
      <EtatRequete chargement={sorties.isPending} erreur={sorties.error} onReessayer={() => void sorties.refetch()} lot="L4-A" fonction="Les sorties du patrimoine">
        {(sorties.data ?? []).length === 0 ? <EtatVide titre="Aucune demande de sortie" icone={LogOut}><p>Aucune demande ne correspond à ce filtre.</p></EtatVide> : <div className={commun.pile}>{(sorties.data ?? []).map((s) => <Demande key={s.id} s={s} />)}</div>}
      </EtatRequete>
    </>
  );
}
